import { Close as CloseIcon } from '@mui/icons-material';
import { Box, Drawer, IconButton, SxProps, Theme, Typography } from '@mui/material';
import React, { useCallback } from 'react';

import { ReactComponent as DragControlIcon } from '../../assets/images/icon-drag-control.svg';
import { CAMPAIGN_ROW_HEIGHT_COMPACT } from '../../constants/campaigns';
import { useCurrency } from '../../contexts/CurrencyProvider';
import variables from '../../contexts/ThemeProvider/variables';
import { ICampaign } from '../../types/campaigns';
import DraggableList from '../DraggableList/DraggableList';
import { FormControls } from '../SummaryComponentMUI/components/common';
import { CampaignListItem } from '../_mui';

interface IProps {
  campaigns: ICampaign[];
  onReorder: (next: ICampaign[]) => void;
  onRemove?: (campaign: ICampaign) => void;
  onCancel: () => void;
  onSave: () => void;
  saveLabel?: string;
  isSaveDisabled?: boolean;
  isSaving?: boolean;
}

const headerStyles: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  mb: 1,
};

const rowStyles: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  px: 1.5,
  py: 0.5,
  backgroundColor: 'background.paper',
  cursor: 'grab',
  '&:active': { cursor: 'grabbing' },
};

const rowStylesSingle: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  px: 1.5,
  py: 0.5,
  backgroundColor: 'background.paper',
};

export const PYGEditSidebar: React.FC<IProps> = ({
  campaigns,
  onReorder,
  onRemove,
  onCancel,
  onSave,
  saveLabel = 'Continue',
  isSaveDisabled,
  isSaving,
}) => {
  const { getCampaignTotal } = useCurrency();
  const isReorderable = campaigns.length >= 2;

  const getCampaignId = useCallback((campaign: ICampaign) => campaign.box_id, []);

  const handleDrawerClose = (_event: React.SyntheticEvent | Event, _reason: 'backdropClick' | 'escapeKeyDown') => {
    if (isSaving) {
      return;
    }
    onCancel();
  };

  const renderCampaignRow = (campaign: ICampaign) => (
    <Box sx={isReorderable ? rowStyles : rowStylesSingle}>
      {isReorderable && <Box component={DragControlIcon} tabIndex={-1} sx={{ flexShrink: 0 }} />}
      <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
        <CampaignListItem
          campaign={campaign}
          total={getCampaignTotal(campaign)}
          onRemove={onRemove ? () => onRemove(campaign) : undefined}
        />
      </Box>
    </Box>
  );

  return (
    <Drawer
      anchor="right"
      open
      onClose={handleDrawerClose}
      slotProps={{
        paper: {
          sx: {
            width: variables.boxSidebarMaxWidthFullHD,
            padding: 6,
            display: 'flex',
            flexDirection: 'column',
            [`@media (max-width: ${variables.breakpointHD})`]: {
              width: variables.boxSidebarMaxWidthHD,
            },
            [`@media (max-width: ${variables.breakpointMobile})`]: {
              width: '100%',
            },
          },
        },
      }}
      aria-labelledby="pyg-edit-sidebar-title"
    >
      <Box sx={headerStyles}>
        <Typography variant="h6" id="pyg-edit-sidebar-title">
          Campaign Options
        </Typography>
        <IconButton onClick={onCancel} aria-label="close" size="small" sx={{ p: 0.5 }} data-action="close_sidebar_btn">
          <CloseIcon fontSize="small" sx={{ width: 20, height: 20 }} />
        </IconButton>
      </Box>
      <Typography variant="body2" sx={{ mb: 3 }}>
        The recipient can select from these campaigns.
      </Typography>

      <Box sx={{ flex: '1 1 auto', overflowY: 'auto', minHeight: 0 }}>
        <DraggableList<ICampaign>
          items={campaigns}
          getId={getCampaignId}
          rowHeight={CAMPAIGN_ROW_HEIGHT_COMPACT}
          isBounded={false}
          readOnly={!isReorderable}
          onDragStop={onReorder}
          render={campaigns.map((campaign) => (
            <Box key={campaign.box_id}>{renderCampaignRow(campaign)}</Box>
          ))}
        />
      </Box>

      <Box sx={{ pt: 2 }}>
        <FormControls
          onCancel={onCancel}
          onSubmit={onSave}
          customText={saveLabel}
          isDisabled={isSaveDisabled}
          isLoading={isSaving}
          submitDataAction="save_pyg_campaign_order_btn"
        />
      </Box>
    </Drawer>
  );
};
